import { cn } from "@/lib/utils";
import * as SelectPrimitive from "@radix-ui/react-select";
import * as Sentry from "@sentry/react";
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Label } from "../shared/Label/Label";

function Select({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue({
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

interface SelectTriggerProps
  extends React.ComponentProps<typeof SelectPrimitive.Trigger> {
  label?: string;
  helperText?: string;
  hasError?: boolean;
  phone?: boolean;
}

function SelectTrigger({
  className,
  size = "default",
  children,
  label,
  helperText,
  phone = false,
  ref,
  hasError,
  ...props
}: SelectTriggerProps & {
  size?: "sm" | "default";
}) {
  return (
    <div className={`grid items-center gap-2 ${!phone ? "w-full" : ""}`}>
      {label && (
        <Label className="!text-white" htmlFor={label}>
          {label}
        </Label>
      )}
      <div className="relative">
        <SelectPrimitive.Trigger
          data-slot="select-trigger"
          data-size={size}
          className={cn(
            "flex h-10 w-full items-center justify-between rounded-md border bg-[#09090B] px-3 py-2 text-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-[#18181B] disabled:border-color-[#27272A] [&>span]:line-clamp-1",
            helperText ? "border-borderError" : "border-colorBorder",
            className
          )}
          {...props}
        >
          {children}
          <SelectPrimitive.Icon asChild>
            <ChevronDownIcon className="size-4 opacity-50" />
          </SelectPrimitive.Icon>
        </SelectPrimitive.Trigger>
      </div>
      {helperText && <p className="text-sm text-error">{helperText}</p>}
    </div>
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn(
        "flex cursor-default items-center justify-center py-1",
        className
      )}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

function SelectContent({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) {
  React.useEffect(() => {
    const portalId = "select-portal-container";
    let container = document.getElementById(portalId);
    if (!container) {
      container = document.createElement("div");
      container.id = portalId;
      container.style.position = "absolute";
      container.style.top = "0";
      container.style.left = "0";
      document.body.appendChild(container);
    }
    container.style.zIndex = "99999";
  }, []);

  return (
    <ErrorBoundary
      onError={(error, info) => {
        Sentry.captureException(error, {
          tags: { component: "Select", area: "SelectContent" },
          extra: {
            componentStack: info?.componentStack,
            dateISO: new Date().toISOString(),
          },
        });
      }}
      fallback={
        <h1 className="text-colorText text-center text-lg font-semibold px-4 flex items-center justify-center my-9">
          <div>
            Ocorreu um erro ao renderizar a aplicação (<strong>Select</strong>
            ), possivelmente causado por alguma extensão do seu navegador.
            <br />
            <span className="text-primary block mt-2">
              Para utilizar a aplicação, recomendamos que você use uma guia
              anônima.
            </span>
          </div>
        </h1>
      }
    >
      <SelectPrimitive.Portal
        container={document.getElementById("select-portal-container")}
      >
        <SelectPrimitive.Content
          data-slot="select-content"
          className={cn(
            "relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
            position === "popper" &&
              "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
            className
          )}
          position={position}
          {...props}
        >
          <SelectScrollUpButton />
          <SelectPrimitive.Viewport
            className={cn(
              "p-1",
              position === "popper" &&
                "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
            )}
          >
            {children}
          </SelectPrimitive.Viewport>
          <SelectScrollDownButton />
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </ErrorBoundary>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn("text-muted-foreground px-2 py-1.5 text-xs", className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        "focus:bg-accent focus:text-accent-foreground [&_svg:not([class*='text-'])]:text-muted-foreground relative flex w-full cursor-default items-center gap-2 rounded-sm py-1.5 pr-8 pl-2 text-sm outline-hidden select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className
      )}
      data-testid="select-option"
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectSeparator({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Separator>) {
  return (
    <SelectPrimitive.Separator
      data-slot="select-separator"
      className={cn("bg-border pointer-events-none -mx-1 my-1 h-px", className)}
      {...props}
    />
  );
}

export {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectScrollDownButton, SelectScrollUpButton, SelectSeparator, SelectTrigger, SelectValue
};
